import { useEffect, useRef, useState } from "react";
import { Briefcase, GraduationCap, Plane, Mic2, Home, PenLine, Volume2, Mic, Square, Loader2, Send, Lightbulb, ArrowLeft, BookmarkPlus, Check, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAudioRecorder } from "@/hooks/useAudioRecorder";
import { voiceOK, ttsOK, langDisplayName } from "@/lib/languages";

const FAMILIES = [
  ["work", Briefcase, ["job", "internship", "promotion", "freelance", "networking"]],
  ["education", GraduationCap, ["university", "scholarship", "oral_exam", "language_exam"]],
  ["travel", Plane, ["visa", "travel_questions", "hotel_problem"]],
  ["public", Mic2, ["media", "presentation_qa", "client_meeting", "pro_intro"]],
  ["personal", Home, ["housing", "doctor", "meet_new", "difficult"]],
  ["custom", PenLine, ["custom"]],
];
const uid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
const Pick = ({ value, options, onChange, testid }) => (
  <div className="flex flex-wrap gap-2">{options.map(([v, label]) => (
    <button key={v} type="button" onClick={() => onChange(v)} data-testid={`${testid}-${v}`}
      className={`nb-tag py-1.5 normal-case tracking-normal ${value === v ? "bg-ink text-white" : "bg-white"}`}>{label}</button>))}</div>
);

function Setup({ prefill, onStarted, targetCode, script }) {
  const { t, locale } = useI18n();
  const [scenario, setScenario] = useState(prefill ? "custom" : "job");
  const [context, setContext] = useState(prefill || "");
  const [length, setLength] = useState("standard");
  const [style, setStyle] = useState("realistic");
  const [busy, setBusy] = useState(false);
  const clientId = useRef(uid());
  const start = async () => {
    if (scenario === "custom" && !context.trim()) return toast.error(t("iv.describeFirst"));
    setBusy(true);
    try { onStarted((await api.post("/friend/interview/start", { scenario, context, length, style, client_id: clientId.current })).data); }
    catch { toast.error(t("iv.retry")); } finally { setBusy(false); }
  };
  return (
    <div className="nb-card p-5 bg-white space-y-4" data-testid="interview-setup">
      <div>
        <h2 className="font-cabinet font-extrabold text-xl">{t("iv.promise")}</h2>
        <p className="text-sm text-gray-600 mt-1">{t("iv.support")} <span className="font-bold" data-testid="interview-target-lang">{langDisplayName(targetCode, locale)}{targetCode === "sr" && script ? ` · ${t(script === "latin" ? "iv.scriptLatin" : "iv.scriptCyrillic")}` : ""}</span></p>
      </div>
      {FAMILIES.map(([fam, Icon, keys]) => (
        <div key={fam}>
          <div className="text-xs font-bold uppercase text-gray-500 mb-1.5 flex items-center gap-1"><Icon className="w-3.5 h-3.5" /> {t(`iv.fam.${fam}`)}</div>
          <Pick value={scenario} onChange={setScenario} testid="interview-scenario" options={keys.map((k) => [k, t(`iv.sc.${k}`)])} />
        </div>
      ))}
      <div>
        <label className="block text-sm font-bold mb-1">{t("iv.contextLabel")}</label>
        <textarea value={context} onChange={(e) => setContext(e.target.value)} maxLength={600} dir="auto" data-testid="interview-context"
          placeholder={scenario === "custom" ? t("iv.customPh") : t("iv.contextPh")} className="nb-input w-full h-20 text-sm" />
      </div>
      <div><div className="text-sm font-bold mb-1">{t("iv.length")}</div>
        <Pick value={length} onChange={setLength} testid="interview-length" options={["quick", "standard", "deep"].map((k) => [k, t(`iv.${k}`)])} /></div>
      <div><div className="text-sm font-bold mb-1">{t("iv.style")}</div>
        <Pick value={style} onChange={setStyle} testid="interview-style" options={["supportive", "realistic", "challenging"].map((k) => [k, t(`iv.${k}`)])} /></div>
      <button onClick={start} disabled={busy} className="nb-btn-primary w-full py-3" data-testid="interview-start">
        {busy ? <><Loader2 className="w-5 h-5 animate-spin" /> {t("iv.starting")}</> : t("iv.start")}</button>
      <p className="text-xs text-gray-500" data-testid="interview-disclaimer">{t("iv.disclaimer")}</p>
    </div>
  );
}

function Answer({ conv, onUpdate, targetCode }) {
  const { t } = useI18n();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("type");
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpIn, setHelpIn] = useState("");
  const [phrase, setPhrase] = useState("");
  const [failed, setFailed] = useState(false);
  const turnId = useRef(uid());
  const { recording, start, stop, audioMime, audioMeta } = useAudioRecorder();
  const send = async () => {
    if (!text.trim() || busy) return;
    setBusy(true); setFailed(false);
    try {
      onUpdate((await api.post(`/friend/interview/${conv.conversation_id}/answer`, { text, turn_id: turnId.current, mode: mode === "speak" ? "voice" : "text" })).data);
      setText(""); setPhrase(""); setHelpOpen(false); turnId.current = uid();
    } catch { setFailed(true); } finally { setBusy(false); }
  };
  const mic = async () => {
    if (!recording) { try { await start(); } catch { toast.error(t("iv.micFail")); setMode("type"); } return; }
    const b64 = await stop();
    if (!b64) { toast.error(t("iv.micFail")); setMode("type"); return; }
    setBusy(true);
    try { const r = await api.post("/friend/live/transcribe", { audio: b64, mime: audioMime.current, meta: audioMeta.current });
      setText((p) => (p ? p + " " : "") + r.data.transcript); }
    catch { toast.error(t("iv.micFail")); setMode("type"); } finally { setBusy(false); }
  };
  const help = async () => {
    if (!helpIn.trim()) return;
    try { setPhrase((await api.post(`/friend/interview/${conv.conversation_id}/help`, { native_text: helpIn })).data.phrase); }
    catch { toast.error(t("iv.retry")); }
  };
  return (
    <div className="nb-card p-4 bg-white space-y-3" data-testid="interview-answer">
      {voiceOK(targetCode) && (
        <div className="flex gap-1">{["type", "speak"].map((m) => (
          <button key={m} onClick={() => setMode(m)} data-testid={`interview-mode-${m}`}
            className={`py-1 px-3 rounded-lg border-2 border-ink text-xs font-bold ${mode === m ? "bg-mint" : "bg-white"}`}>{t(`iv.${m}`)}</button>))}</div>
      )}
      <textarea value={text} onChange={(e) => setText(e.target.value)} dir="auto" placeholder={t("iv.answerPh")} className="nb-input w-full h-24" data-testid="interview-answer-input" />
      {failed && <div className="text-sm text-coral font-bold flex items-center gap-2" data-testid="interview-error">{t("iv.retry")}
        <button onClick={send} className="underline" data-testid="interview-retry">{t("iv.retryBtn")}</button></div>}
      <div className="flex flex-wrap gap-2 items-center">
        {mode === "speak" && voiceOK(targetCode) && (
          <button onClick={mic} disabled={busy && !recording} className={`nb-btn-outline py-2 px-3 text-sm ${recording ? "bg-coral text-white" : ""}`} data-testid="interview-mic">
            {recording ? <><Square className="w-4 h-4" /> {t("iv.recording")}</> : <Mic className="w-4 h-4" />}</button>)}
        <button onClick={() => setHelpOpen((v) => !v)} className="nb-btn-outline py-2 px-3 text-sm" data-testid="interview-help-toggle"><Lightbulb className="w-4 h-4" /> {t("iv.help")}</button>
        <button onClick={send} disabled={busy || !text.trim()} className="nb-btn-primary py-2 px-4 text-sm ms-auto" data-testid="interview-send">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 rtl:-scale-x-100" />} {t("iv.send")}</button>
      </div>
      {helpOpen && (
        <div className="bg-butter/60 border-2 border-ink rounded-xl p-3 space-y-2" data-testid="interview-help">
          <div className="flex gap-2"><input value={helpIn} onChange={(e) => setHelpIn(e.target.value)} dir="auto" placeholder={t("iv.helpPh")} className="nb-input flex-1 text-sm min-w-0" data-testid="interview-help-input" />
            <button onClick={help} className="nb-btn-outline px-3 text-sm" data-testid="interview-help-go">{t("iv.helpGo")}</button></div>
          {phrase && <><p className="font-bold" dir="auto" data-testid="interview-help-phrase">{phrase}</p><p className="text-xs text-gray-600">{t("iv.helpNote")}</p></>}
        </div>
      )}
    </div>
  );
}

function Report({ conv, onUpdate }) {
  const { t } = useI18n();
  const rep = conv.interview.report;
  const [saved, setSaved] = useState(() => Object.fromEntries((rep?.phrases || []).map((p, i) => [i, !!p.saved])));
  const [failed, setFailed] = useState(false);
  const build = async () => { setFailed(false); try { onUpdate((await api.post(`/friend/interview/${conv.conversation_id}/report`)).data); } catch { setFailed(true); } };
  useEffect(() => { if (!rep) build(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (rep) setSaved(Object.fromEntries((rep.phrases || []).map((p, i) => [i, !!p.saved]))); }, [rep]);
  if (!rep) return <div className="nb-card p-6 bg-white text-center" data-testid="interview-report-loading">
    {failed ? <><p className="text-coral font-bold">{t("iv.retry")}</p><button onClick={build} className="nb-btn-primary mt-3 px-4 py-2" data-testid="interview-report-retry">{t("iv.retryBtn")}</button></>
      : <><Loader2 className="w-8 h-8 animate-spin mx-auto" /><p className="font-bold mt-2">{t("iv.building")}</p></>}</div>;
  const save = async (i) => { try { await api.post(`/friend/interview/${conv.conversation_id}/phrases/save`, { index: i }); setSaved((s) => ({ ...s, [i]: true })); toast.success(t("iv.saved")); } catch { toast.error(t("iv.retry")); } };
  const dot = { strong: "bg-mint", developing: "bg-butter", needs_practice: "bg-peach" };
  const List = ({ title, items, testid }) => items?.length ? <div data-testid={testid}><h3 className="font-cabinet font-bold">{title}</h3>
    <ul className="list-disc ps-5 text-sm mt-1 space-y-1">{items.map((x, i) => <li key={i} dir="auto">{x}</li>)}</ul></div> : null;
  return (
    <div className="nb-card p-5 bg-white space-y-4" data-testid="interview-report">
      <div><h3 className="font-cabinet font-bold">{t("iv.overall")}</h3><p className="text-sm mt-1" dir="auto" data-testid="interview-overall">{rep.overall}</p></div>
      <div><h3 className="font-cabinet font-bold mb-2">{t("iv.areasT")}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">{Object.entries(rep.areas).map(([k, v]) => (
          <div key={k} className="flex items-center justify-between gap-2 border-2 border-ink rounded-xl px-3 py-2 text-sm" data-testid={`interview-area-${k}`}>
            <span className="font-bold">{t(`iv.areas.${k}`)}</span><span className={`nb-tag normal-case tracking-normal ${dot[v]}`}>{t(`iv.lv.${v}`)}</span></div>))}</div></div>
      <List title={t("iv.worked")} items={rep.worked} testid="interview-worked" />
      <List title={t("iv.improve")} items={rep.improve} testid="interview-improve" />
      {rep.better?.length > 0 && <div data-testid="interview-better"><h3 className="font-cabinet font-bold">{t("iv.better")}</h3>
        {rep.better.map((b, i) => <div key={i} className="text-sm mt-2 border-s-4 border-mint ps-3"><p className="text-gray-500" dir="auto">{b.learner}</p><p className="font-bold" dir="auto">{b.improved}</p></div>)}</div>}
      {rep.phrases?.length > 0 && <div data-testid="interview-phrases"><h3 className="font-cabinet font-bold">{t("iv.phrases")}</h3>
        {rep.phrases.map((p, i) => <div key={i} className="flex items-center justify-between gap-2 mt-2 text-sm">
          <span className="min-w-0"><b dir="auto">{p.phrase}</b> <span className="text-gray-500" dir="auto">· {p.meaning}</span></span>
          <button onClick={() => save(i)} disabled={saved[i]} className="nb-btn-outline py-1 px-2 text-xs shrink-0" data-testid={`interview-save-phrase-${i}`}>
            {saved[i] ? <Check className="w-3 h-3" /> : <BookmarkPlus className="w-3 h-3" />} {saved[i] ? t("iv.saved") : t("iv.save")}</button></div>)}</div>}
    </div>
  );
}

function Session({ conv, setConv }) {
  const targetCode = conv.target_language;
  const { t } = useI18n();
  const [playing, setPlaying] = useState(null);
  const [showReport, setShowReport] = useState(!!conv.interview.report);
  const iv = conv.interview;
  const answered = conv.messages.filter((m) => m.role === "learner").length;
  const listen = async (i) => {
    setPlaying(i);
    try { const r = await api.post(`/friend/interview/${conv.conversation_id}/listen/${i}`); await new Audio(`data:audio/mp3;base64,${r.data.audio}`).play(); }
    catch { /* audio is optional; text stays authoritative */ } finally { setPlaying(null); }
  };
  return (
    <div className="space-y-3" data-testid="interview-session">
      <div className="text-xs font-bold text-gray-500" data-testid="interview-progress">{t("iv.progress", { n: Math.min(answered + (iv.status === "active" ? 1 : 0), iv.total), total: iv.total })}</div>
      {conv.messages.map((m, i) => (
        <div key={i} className={`flex ${m.role === "learner" ? "justify-end" : "justify-start"}`}>
          <div className={`max-w-[85%] rounded-2xl border-2 border-ink px-4 py-3 ${m.role === "learner" ? "bg-lavender rounded-se-none" : "bg-white rounded-ss-none"}`} data-testid={`interview-msg-${i}`}>
            <p dir="auto" className="whitespace-pre-line">{m.text}</p>
            {m.role === "interviewer" && ttsOK(targetCode) && (
              <button onClick={() => listen(i)} disabled={playing !== null} className="text-xs font-bold mt-1 inline-flex items-center gap-1" data-testid={`interview-listen-${i}`}>
                {playing === i ? <Loader2 className="w-3 h-3 animate-spin" /> : <Volume2 className="w-3 h-3" />} {t("iv.listen")}</button>)}
          </div>
        </div>
      ))}
      {iv.status === "active" && <Answer conv={conv} onUpdate={setConv} targetCode={targetCode} />}
      {iv.status !== "active" && !showReport && (
        <div className="nb-card p-4 bg-mint text-center" data-testid="interview-finished"><p className="font-bold">{t("iv.finished")}</p>
          <button onClick={() => setShowReport(true)} className="nb-btn-primary mt-3 px-5 py-2" data-testid="interview-see-report">{t("iv.seeReport")}</button></div>)}
      {showReport && <Report conv={conv} onUpdate={setConv} />}
    </div>
  );
}

export function InterviewPractice({ targetCode, script, prefill, onExit }) {
  const { t, locale } = useI18n();
  const [conv, setConv] = useState(null);
  const [rows, setRows] = useState([]);
  const [setupKey, setSetupKey] = useState(0);
  const load = () => api.get("/friend/interview/list").then((r) => setRows(r.data.interviews)).catch(() => {});
  useEffect(() => { if (!conv) load(); }, [conv]);
  const open = async (id) => { try { setConv((await api.get(`/friend/interview/${id}`)).data); } catch { toast.error(t("iv.retry")); } };
  return (
    <div className="space-y-4" data-testid="interview-practice">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-cabinet font-extrabold text-2xl">{t("iv.cardTitle")}</h2>
        <button onClick={conv ? () => setConv(null) : onExit} className="text-sm font-bold inline-flex items-center gap-1" data-testid="interview-back"><ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {t("iv.back")}</button>
      </div>
      {conv ? <Session conv={conv} setConv={setConv} /> : <>
        <Setup key={setupKey} prefill={prefill} onStarted={setConv} targetCode={targetCode} script={script} />
        <div data-testid="interview-history"><h3 className="font-cabinet font-bold mb-2">{t("iv.history")}</h3>
          {!rows.length ? <p className="text-sm text-gray-500">{t("iv.empty")}</p> : rows.map((r) => (
            <div key={r.conversation_id} className="nb-card p-3 bg-white mb-2 flex items-center justify-between gap-2" data-testid={`interview-row-${r.conversation_id}`}>
              <div className="min-w-0 text-sm"><div className="font-bold truncate">{t(`iv.sc.${r.interview?.scenario}`)} <span dir="ltr" className="text-gray-500">· {langDisplayName(r.target_language, locale)} · {r.level}</span></div>
                <div className="text-xs text-gray-500">{new Date(r.created_at).toLocaleDateString(locale)} · {t(`iv.st.${r.interview?.status}`)}</div>
                {r.interview?.report?.overall && <div className="text-xs text-gray-600 truncate" dir="auto">{r.interview.report.overall}</div>}</div>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => open(r.conversation_id)} className="nb-btn-outline py-1 px-2 text-xs" data-testid={`interview-open-${r.conversation_id}`}>{r.interview?.status === "completed" ? t("iv.view") : t("iv.resume")}</button>
                {r.interview?.status !== "completed" && <button onClick={() => { setSetupKey((k) => k + 1); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="nb-btn-outline py-1 px-2 text-xs" title={t("iv.startOver")} aria-label={t("iv.startOver")} data-testid={`interview-restart-${r.conversation_id}`}><RotateCcw className="w-3 h-3" /></button>}
              </div>
            </div>))}
        </div></>}
    </div>
  );
}

// Subtle Friend-chat suggestion when a message clearly mentions an upcoming interview-like situation.
const EVENT = /\b(interview|entrevista|entretien|vorstellungsgespr|bewerbungsgespr|oral exam|examen oral|admission|mündliche|prüfung)|مقابلة|امتحان/i;
const SOON = /\b(tomorrow|next week|soon|on (monday|tuesday|wednesday|thursday|friday)|mañana|pronto|la semana que viene|demain|bientôt|la semaine prochaine|morgen|bald|nächste woche|übermorgen)|غدًا|غدا|قريبًا|الأسبوع القادم/i;
export const mentionsUpcomingInterview = (text) => EVENT.test(text || "") && SOON.test(text || "");
